import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useGetList, useRecordContext, useTranslate } from "ra-core";
import type { Identifier } from "ra-core";
import { Badge } from "@/components/ui/badge";

import type { Project, Sale, SalesProject } from "../types";

// design-system.md section 8: a designed empty value, never a raw
// "null"/"undefined".
const EMPTY_VALUE = "—";

export interface MembershipView {
  id: Identifier;
  projectId: Identifier;
  projectName: string;
  isLider: boolean;
}

interface SalesProjectsListData {
  membershipsBySalesId: Map<Identifier, MembershipView[]>;
  isPending: boolean;
}

const SalesProjectsListContext = createContext<SalesProjectsListData | null>(
  null,
);

/**
 * Fetches every ACTIVE (`ended_at IS NULL`) `sales_projects` row and every
 * project name in exactly one query each -- never one request per team-list
 * row (the ticket's `performance` non-functional requirement, Almikia Epic
 * E3b-2 TASK-086) -- then groups them by `sales_id` and provides the result
 * to every `SalesProjectsField` below it. Mount once around the team list
 * (`SalesList.tsx`), not per row.
 */
export const SalesProjectsListProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data: memberships, isPending: membershipsPending } =
    useGetList<SalesProject>("sales_projects", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: { "ended_at@is": null },
    });
  const { data: projects, isPending: projectsPending } = useGetList<Project>(
    "projects",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: {},
    },
  );

  const membershipsBySalesId = useMemo(() => {
    const projectsById = new Map(
      (projects ?? []).map((project) => [project.id, project]),
    );
    const grouped = new Map<Identifier, MembershipView[]>();
    for (const membership of memberships ?? []) {
      const project = projectsById.get(membership.project_id);
      const forSale = grouped.get(membership.sales_id) ?? [];
      forSale.push({
        id: membership.id,
        projectId: membership.project_id,
        projectName: project?.name ?? EMPTY_VALUE,
        isLider: membership.is_lider_for_project,
      });
      grouped.set(membership.sales_id, forSale);
    }
    return grouped;
  }, [memberships, projects]);

  return (
    <SalesProjectsListContext.Provider
      value={{
        membershipsBySalesId,
        isPending: membershipsPending || projectsPending,
      }}
    >
      {children}
    </SalesProjectsListContext.Provider>
  );
};

/**
 * "%{name} (Líder BPO)" badge for an active membership, shared between the
 * team list (`SalesProjectsField` below) and the edit page
 * (`SalesProjectsInput.tsx`) so both render the exact same marker.
 */
export const MembershipBadge = ({
  membership,
}: {
  membership: MembershipView;
}) => {
  const translate = useTranslate();
  return (
    <Badge variant={membership.isLider ? "secondary" : "outline"}>
      {membership.isLider
        ? translate("resources.sales.projects.lider_bpo_marker", {
            name: membership.projectName,
          })
        : membership.projectName}
    </Badge>
  );
};

/**
 * One team-list row's active project memberships (Almikia Epic E3b-2,
 * TASK-086): every role that reaches the team list (`sales` RLS reads `true`
 * for everyone, see ADR-ee2f59a9-TASK-079) sees this read-only -- editing
 * lives only on the edit page, gated there to direccion.
 */
export const SalesProjectsField = () => {
  const record = useRecordContext<Sale>();
  const context = useContext(SalesProjectsListContext);
  if (!record || !context) return null;

  if (context.isPending) {
    return <span className="text-muted-foreground">…</span>;
  }

  const memberships = context.membershipsBySalesId.get(record.id) ?? [];
  if (memberships.length === 0) {
    return <span>{EMPTY_VALUE}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {memberships.map((membership) => (
        <MembershipBadge key={membership.id} membership={membership} />
      ))}
    </div>
  );
};
